<template>
  <UPage>
    <UPageHeader
      :title="(transactionDetails.fldBez as string) || 'Vorgangsdetails'"
      :description="`Belegnummer: ${fldBelegNr}`"
    >
      <template #links>
        <UButton
          icon="i-heroicons-arrow-left"
          label="Zurück zu Vorgängen"
          variant="ghost"
          @click="$router.push('/microtech-hub/transactions')"
        />
      </template>
    </UPageHeader>

    <UPageBody>
      <!-- Column Configuration Modal -->
      <ColumnSelectionModal
        ref="columnModal"
        :all-columns="allPossibleColumns"
        :initial-selected="selectedTabColumnsMapped"
        :loading="fetchingSchema"
        :title="modalTitle"
        :description="modalDescription"
        @save="onSaveColumns"
        @reset="resetToDefault"
      />

      <UTabs v-model="activeTab" :items="tabs" class="w-full">
        <template #details>
          <UCard>
            <template #header>
              <div class="flex items-center justify-between">
                <h3 class="text-base font-semibold">Stammdaten</h3>
                <UButton
                  color="neutral"
                  icon="i-heroicons-cog-6-tooth"
                  label="Felder anpassen"
                  size="xs"
                  variant="ghost"
                  @click="openColumnModal('details')"
                />
              </div>
            </template>
            <div v-if="loading" class="flex justify-center py-10">
              <UIcon class="w-10 h-10 animate-spin text-muted" name="i-heroicons-arrow-path" />
            </div>
            <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
              <div
                v-for="field in detailFields"
                :key="field.accessorKey"
                class="border-b border-default pb-2"
              >
                <p class="text-xs font-medium text-muted uppercase tracking-wider">
                  {{ field.label }}
                </p>
                <p class="mt-1 text-sm text-highlighted">
                  {{ resolveValue(transactionDetails, field) || '-' }}
                </p>
              </div>
            </div>
          </UCard>
        </template>

        <template #dokumente>
          <PortalTable
            ref="dokumenteTable"
            :columns-loading="fetchingSchema"
            :data="relatedData.dokumente"
            :loading="loadingRelated.dokumente"
            :selected-columns="selectedTabColumns('dokumente')"
            :all-columns="availableColumns('dokumente')"
            :total="relatedData.dokumente.length"
            :sort-storage-key="DOC_SORT_STORAGE_KEY"
            class="w-full max-h-[60vh]"
            highlight-row-key="fldGUID"
            :highlight-row-value="$route.query.highlightGUID as string"
            @select="(row) => downloadDocument(row.original)"
            @update:columns="onSaveColumns"
            @reset:columns="resetToDefault"
          >
            <template #header>
              <div class="flex items-center justify-between p-4 border-b border-default">
                <h3 class="text-base font-semibold">Dokumente</h3>
                <UButton
                  color="neutral"
                  icon="i-heroicons-cog-6-tooth"
                  label="Felder anpassen"
                  size="xs"
                  variant="ghost"
                  @click="openColumnModal('dokumente')"
                />
              </div>
            </template>
            <template #body-bottom>
              <div
                v-if="hasNextPageByTab.dokumente"
                v-intersection-observer="() => fetchRelatedConRead('dokumente', true)"
                class="h-1 w-full"
              />
            </template>
          </PortalTable>
        </template>

        <template #vatvalidation>
          <VatTransactionValidationPage
            :transaction-nr="fldBelegNr"
            :embedded="true"
            :customer="customerNo"
            :tenant="tenantName"
          />
        </template>
      </UTabs>
    </UPageBody>
  </UPage>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import PortalTable from '@/components/PortalTable.vue'
import ColumnSelectionModal from '@/components/ColumnSelectionModal.vue'
import VatTransactionValidationPage from '@/pages/VatTransactionValidationPage.vue'
import type { FieldInfo } from '@/utils/graphql-introspection'
import { fetchRelationFields, fetchTableFields } from '@/utils/graphql-introspection'
import { useAuthStore } from '@/stores/auth'
import { useDocumentDownload } from '@/composables/useDocumentDownload'

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()

const customerNo = computed(
  () => props.customerNo || authStore.userInfo?.activeTenant?.customerNo || '',
)
const tenantName = computed(
  () => props.tenantName || authStore.userInfo?.activeTenant?.tenantName || '',
)

const DOC_SORT_STORAGE_KEY = computed(
  () =>
    `cc_tr_det_doc_sort_${customerNo.value}_${tenantName.value}_${props.isArchive === 'true' ? 'archive' : 'open'}`,
)

const props = defineProps<{
  customerNo: string
  tenantName: string
  fldBelegNr: string
  isArchive?: string
}>()

const loading = ref(true)
const transactionDetails = ref<Record<string, unknown>>({})
const resolveSelectedColumns = (tabKey: string) => {
  const selected = selectedColumnsByTab.value[tabKey] || []
  const available = availableColumnsByTab.value[tabKey] || []

  return selected.map((col) => {
    const found = available.find((a) => (a.accessorKey || a.name) === (col.accessorKey || col.name))
    return found || col
  })
}

const detailFields = computed(() => resolveSelectedColumns('details'))
const activeTab = ref((route.query.tab as string) || 'details')
watch(
  () => route.query.tab,
  (newTab) => {
    if (newTab && typeof newTab === 'string') {
      activeTab.value = newTab
    } else {
      activeTab.value = 'details'
    }
  },
)

const tabs = [
  { label: 'Details', slot: 'details', value: 'details' },
  { label: 'Dokumente', slot: 'dokumente', value: 'dokumente' },
  { label: 'USt-IdNr.-Prüfung', slot: 'vatvalidation', value: 'vatvalidation' },
]

const loadingRelated = ref<Record<string, boolean>>({
  dokumente: false,
})

const relatedData = ref<Record<string, Record<string, unknown>[]>>({
  dokumente: [],
})

// Pagination state for conRead tabs
const endCursorByTab = ref<Record<string, string>>({
  dokumente: '',
})
const hasNextPageByTab = ref<Record<string, boolean>>({
  dokumente: false,
})
const limit = 100

// Column configurations
const availableColumnsByTab = ref<Record<string, FieldInfo[]>>({
  details: [],
  dokumente: [],
})

const selectedColumnsByTab = ref<Record<string, FieldInfo[]>>({
  details: [],
  dokumente: [],
})

const columnModal = ref()
const modalTabKey = ref('details')
const modalTitle = computed(
  () => `Spalten anpassen für ${tabs.find((t) => t.value === modalTabKey.value)?.label || ''}`,
)
const modalDescription = ref('Passen Sie die Anzeige der Spalten für die aktuelle Tab-Ansicht an.')

const allPossibleColumns = computed(() => {
  const fields = availableColumnsByTab.value[modalTabKey.value] || []
  return fields.map((f) => ({
    key: f.accessorKey || f.name,
    label: f.label || f.description || f.name,
    locked: isMandatory(f, modalTabKey.value),
  }))
})

const selectedTabColumnsMapped = computed(() => {
  const fields = selectedColumnsByTab.value[modalTabKey.value] || []
  return fields.map((f) => ({
    key: f.accessorKey || f.name || '',
    label: f.label || f.description || f.name || '',
    locked: isMandatory(f, modalTabKey.value),
    graphql: f.graphql,
  }))
})

function selectedTabColumns(tab: string) {
  const fields = selectedColumnsByTab.value[tab] || []
  return fields.map((f) => ({
    key: f.accessorKey || f.name || '',
    label: f.label || f.description || f.name || '',
    locked: isMandatory(f, tab),
    graphql: f.graphql,
  }))
}

function availableColumns(tab: string) {
  const fields = availableColumnsByTab.value[tab] || []
  return fields.map((f) => ({
    key: f.accessorKey || f.name || '',
    label: f.label || f.description || f.name || '',
    graphql: f.graphql,
  }))
}

const onSaveColumns = (newCols: { key: string; label: string }[]) => {
  const key = modalTabKey.value
  const available = availableColumnsByTab.value[key] || []

  // Map back to FieldInfo
  selectedColumnsByTab.value[key] = newCols
    .map((nc) => {
      return available.find((a) => (a.accessorKey || a.name) === nc.key)
    })
    .filter(Boolean) as FieldInfo[]

  saveConfig(key)
}

const fetchingSchema = ref(false)
const schemaFetchedByTab = ref<Record<string, boolean>>({
  details: false,
  dokumente: false,
})
const configLoaded = ref(false)

const openColumnModal = async (tabKey?: string) => {
  const key = tabKey || activeTab.value
  modalTabKey.value = key
  columnModal.value?.open()

  if (!schemaFetchedByTab.value[key]) {
    await fetchSchemaForTab(key)
  }
}

const isMandatory = (col: FieldInfo, tabKey?: string) => {
  const key = tabKey || modalTabKey.value
  if (key === 'details') return col.accessorKey === 'fldBelegNr'
  return false
}

const { downloadDocument } = useDocumentDownload()

const resetToDefault = () => {
  const key = modalTabKey.value
  const storageKey = `cc_tr_det_cols_${customerNo.value}_${tenantName.value}_${props.isArchive === 'true' ? 'archive' : 'open'}_${key}`
  localStorage.removeItem(storageKey)
  initializeDefaultColumns(key)
}

const saveConfig = (tabKey: string) => {
  const currentCols = selectedColumnsByTab.value[tabKey]
  if (currentCols && currentCols.length > 0) {
    const storageKey = `cc_tr_det_cols_${customerNo.value}_${tenantName.value}_${props.isArchive === 'true' ? 'archive' : 'open'}_${tabKey}`
    localStorage.setItem(storageKey, JSON.stringify(currentCols))
  }
}

const loadConfig = (tabKey: string) => {
  const storageKey = `cc_tr_det_cols_${customerNo.value}_${tenantName.value}_${props.isArchive === 'true' ? 'archive' : 'open'}_${tabKey}`
  const saved = localStorage.getItem(storageKey)
  if (saved) {
    try {
      const parsed = JSON.parse(saved)
      if (Array.isArray(parsed) && parsed.length > 0) {
        selectedColumnsByTab.value[tabKey] = parsed
      }
    } catch (e) {
      console.error(`Failed to load config for ${tabKey}`, e)
    }
  }
}

const fetchSchemaForTab = async (tabKey: string) => {
  if (!customerNo.value || !tenantName.value) return
  fetchingSchema.value = true
  try {
    let tableName = ''
    let directTypeName = ''
    if (tabKey === 'details')
      tableName = props.isArchive === 'true' ? 'tblTransactionsArchive' : 'tblTransactions'
    else if (tabKey === 'dokumente') directTypeName = 'DocumentRowQueryRead'

    if (!tableName && !directTypeName) return

    const fields = directTypeName
      ? await fetchRelationFields(directTypeName, customerNo.value, tenantName.value)
      : await fetchTableFields(tableName, customerNo.value, tenantName.value)

    const filteredFields = fields.filter((f) => !f.name.startsWith('_') && !f.name.endsWith('_int'))

    if (filteredFields.length === 0) {
      schemaFetchedByTab.value[tabKey] = true
      initializeDefaultColumns(tabKey)
      return
    }

    const dynamicColumns: FieldInfo[] = []

    filteredFields.forEach((f: FieldInfo) => {
      if (!f.isRelation && !f.name.startsWith('_') && !f.name.startsWith('dbg')) {
        dynamicColumns.push({
          ...f,
          accessorKey: f.name,
          header: f.label || f.description || f.name,
          label: f.label || f.description || f.name,
          graphql: f.graphql || f.name,
        })
      }
    })

    if (tabKey === 'dokumente') {
      for (const f of filteredFields) {
        if (f.isRelation && f.name.startsWith('row')) {
          try {
            const relFields = await fetchRelationFields(f.type, customerNo.value, tenantName.value)
            relFields.forEach((rf) => {
              if (!rf.isRelation && !rf.name.startsWith('_')) {
                dynamicColumns.push({
                  name: rf.name,
                  type: rf.type,
                  kind: rf.kind,
                  isRelation: false,
                  accessorKey: `${f.name}_${rf.name}`,
                  header: rf.label
                    ? `${f.label || f.description || f.name}: ${rf.label}`
                    : `${f.description || f.label || f.name}: ${rf.description || rf.label || rf.name}`,
                  label: rf.label
                    ? `${f.label || f.description || f.name}: ${rf.label}`
                    : `${f.description || f.label || f.name}: ${rf.description || rf.label || rf.name}`,
                  graphql: rf.graphql ? `${f.name} { ${rf.graphql} }` : `${f.name} { ${rf.name} }`,
                })
              }
            })
          } catch (e) {
            console.warn(`Failed to fetch relation fields for ${f.name} (${f.type})`, e)
          }
        }
      }
    }

    availableColumnsByTab.value[tabKey] = dynamicColumns
    schemaFetchedByTab.value[tabKey] = true

    if (selectedColumnsByTab.value[tabKey].length > 0) {
      selectedColumnsByTab.value[tabKey] = selectedColumnsByTab.value[tabKey].map((sc) => {
        const found = dynamicColumns.find(
          (c) => (c.accessorKey || c.name) === (sc.accessorKey || sc.name),
        )
        if (found) {
          return { ...sc, label: found.label, header: found.label, graphql: found.graphql }
        }
        return sc
      })
    }

    const currentSelected = selectedColumnsByTab.value[tabKey] || []
    if (currentSelected.length > 0) {
      selectedColumnsByTab.value[tabKey] = currentSelected.filter((sel: FieldInfo) =>
        dynamicColumns.some((avail) => avail.accessorKey === sel.accessorKey),
      )
      if (selectedColumnsByTab.value[tabKey].length === 0) {
        initializeDefaultColumns(tabKey)
      }
    } else {
      initializeDefaultColumns(tabKey)
    }
  } finally {
    fetchingSchema.value = false
  }
}

const initializeDefaultColumns = (tabKey: string) => {
  const avail = availableColumnsByTab.value[tabKey] || []
  let defaults: string[] = []

  if (tabKey === 'details') {
    defaults = [
      'fldAdrNr',
      'fldBelegNr',
      'fldArt',
      'fldSuchBeg',
      'fldPrjNr',
      'fldDat',
      'fldPrio',
      'fldVerk',
      'acoGPreis.totalNetAmount',
    ]
  } else if (tabKey === 'dokumente') {
    defaults = ['fldNa', 'fldExt']
  }

  const found =
    avail.length > 0 ? avail.filter((c: FieldInfo) => defaults.includes(c.accessorKey || '')) : []

  if (found.length > 0) {
    found.sort((a, b) => {
      const indexA = defaults.indexOf(a.accessorKey || '')
      const indexB = defaults.indexOf(b.accessorKey || '')
      return indexA - indexB
    })
  }

  if (tabKey === 'details') {
    const mandatoryKey = 'fldBelegNr'
    if (!found.some((c: FieldInfo) => c.accessorKey === mandatoryKey)) {
      const mand = avail.find((c: FieldInfo) => c.accessorKey === mandatoryKey)
      if (mand) found.unshift(mand)
    }
  }

  selectedColumnsByTab.value[tabKey] = found.length > 0 ? found : avail.slice(0, 5)
}

const buildGraphQLQuery = (tabKey: string) => {
  const selected = selectedColumnsByTab.value[tabKey] || []
  const customSelections = new Set<string>()
  const tree: Record<string, unknown> = {}

  if (tabKey === 'details') {
    tree['fldBelegNr'] = {}
  }

  if (tabKey === 'dokumente') {
    tree['fldExt'] = {}
    tree['fldID'] = {}
    tree['fldGUID'] = {}
  }

  selected.forEach((col: FieldInfo) => {
    if (!col.graphql) return
    if (col.graphql.includes('{')) {
      customSelections.add(col.graphql)
    } else {
      const parts = col.graphql.split('.')
      let current = tree as Record<string, unknown>
      parts.forEach((part: string) => {
        if (!current[part]) current[part] = {} as Record<string, unknown>
        current = current[part] as Record<string, unknown>
      })
    }
  })

  const stringifyFields = (obj: Record<string, unknown>): string => {
    const keys = Object.keys(obj)
    if (keys.length === 0) return ''
    return keys
      .map((key) => {
        const subObj = obj[key] as Record<string, unknown>
        const subFields = stringifyFields(subObj)
        return subFields ? `${key} { ${subFields} }` : key
      })
      .join('\n')
  }

  const baseSelection = stringifyFields(tree)
  return [baseSelection, ...Array.from(customSelections)].filter((s) => s).join('\n')
}

const fetchTransactionDetails = async () => {
  loading.value = true

  if (!schemaFetchedByTab.value.details) {
    await fetchSchemaForTab('details')
  }

  const selectionSet = buildGraphQLQuery('details')
  const isArchiveVal = props.isArchive === 'true'
  const rootField = isArchiveVal ? 'tblTransactionsArchive' : 'tblTransactions'
  const getQuery = (field: string) => `
    query {
      ${field} {
        rowRead(kf1BelegNr: { string: "${props.fldBelegNr}" }) {
          ${selectionSet}
        }
      }
    }
  `

  try {
    const response = await axios.post('/api/microtech/v1/proxy/graphql', {
      query: getQuery(rootField),
      variables: {},
    })

    let row = response.data?.data?.[rootField]?.rowRead

    if (!row && !isArchiveVal) {
      // Fallback to archive
      const archiveField = 'tblTransactionsArchive'
      const archiveResponse = await axios.post('/api/microtech/v1/proxy/graphql', {
        query: getQuery(archiveField),
        variables: {},
      })
      row = archiveResponse.data?.data?.[archiveField]?.rowRead
      if (row) {
        router.replace({ query: { ...route.query, isArchive: 'true' } })
      }
    }

    if (row) {
      transactionDetails.value = row
    }
  } catch (error) {
    console.error('Failed to fetch transaction details:', error)
  } finally {
    loading.value = false
  }
}

const resolveValue = (node: Record<string, unknown>, col: FieldInfo) => {
  if (!col || !col.graphql) return node?.[col?.accessorKey || '']

  if (col.graphql.includes('{')) {
    const parts = col.graphql.split(/[\s\{\},]+/).filter(Boolean)
    let val: unknown = node
    for (const part of parts) {
      val = (val as Record<string, unknown>)?.[part]
      if (val === undefined || val === null) break
    }
    return val
  }

  return node?.[col.graphql] || node?.[col.accessorKey || '']
}

const fetchRelatedConRead = async (tabKey: string, loadMore = false) => {
  if (loadMore && !hasNextPageByTab.value[tabKey]) return
  if (loadingRelated.value[tabKey]) return

  if (!schemaFetchedByTab.value[tabKey]) {
    await fetchSchemaForTab(tabKey)
  }

  const selectionSet = buildGraphQLQuery(tabKey)
  let rootField = ''
  if (tabKey === 'dokumente') {
    rootField = 'tblDocuments'
  }

  if (!rootField) return

  const afterParam =
    loadMore && endCursorByTab.value[tabKey] ? `, after: "${endCursorByTab.value[tabKey]}"` : ''
  const filter = `allBetween: { byVogBelegNr: { kf1VogBelegNr: { string: "${props.fldBelegNr}" } } }`

  const query = `
    query {
      ${rootField} {
        conRead(
          first: ${limit}
          ${afterParam}
          ${filter}
        ) {
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              ${tabKey === 'dokumente' ? 'fldID\nfldGUID' : ''}
              ${selectionSet}
            }
          }
        }
      }
    }
  `

  loadingRelated.value[tabKey] = true
  try {
    const response = await axios.post('/api/microtech/v1/proxy/graphql', { query, variables: {} })

    const conRead = response.data?.data?.[rootField]?.conRead
    if (conRead) {
      const newRows = conRead.edges.map((edge: { node: Record<string, unknown> }) => edge.node)
      if (loadMore) {
        relatedData.value[tabKey] = [...relatedData.value[tabKey], ...newRows]
      } else {
        relatedData.value[tabKey] = newRows
      }
      endCursorByTab.value[tabKey] = conRead.pageInfo.endCursor
      hasNextPageByTab.value[tabKey] = conRead.pageInfo.hasNextPage
    }
  } catch (error) {
    console.error(`Failed to fetch ${tabKey}:`, error)
  } finally {
    loadingRelated.value[tabKey] = false
  }
}

watch(activeTab, (val) => {
  if (route.query.tab !== (val === 'details' ? undefined : val)) {
    router.push({ query: { ...route.query, tab: val === 'details' ? undefined : val } })
  }

  const key = val
  if (key === 'details') {
    if (Object.keys(transactionDetails.value).length === 0) fetchTransactionDetails()
  } else if (key === 'dokumente') {
    if (relatedData.value[key].length === 0) {
      fetchRelatedConRead(key)
    }
  }
})

watch(
  () => props.isArchive,
  () => {
    schemaFetchedByTab.value.details = false
    loadConfig('details')
    loadConfig('dokumente')
    fetchTransactionDetails()
    if (activeTab.value === 'dokumente') {
      fetchRelatedConRead('dokumente')
    }
  },
)

watch(
  selectedColumnsByTab,
  () => {
    if (!configLoaded.value) return
    const key = activeTab.value
    if (key === 'details') fetchTransactionDetails()
    else if (key === 'dokumente') {
      fetchRelatedConRead(key)
    }
  },
  { deep: true },
)

onMounted(async () => {
  const initData = async () => {
    loadConfig('details')
    loadConfig('dokumente')
    configLoaded.value = true
    await fetchTransactionDetails()
  }

  if (customerNo.value && tenantName.value) {
    await initData()
  }

  watch([customerNo, tenantName], async () => {
    if (customerNo.value && tenantName.value) {
      await initData()
    }
  })
})
</script>
